import { useEffect, useRef, useState } from "react";
import type { JobDetail, JobPatchRequest } from "../../types";
import { jobsService } from "../../services/jobs";
import type { DuplicateCheckResult } from "../../services/jobs";
import ConfirmModal from "../modals/ConfirmModal";

interface Props {
  job: JobDetail;
  onDirtyChange: (dirty: boolean) => void;
  saving: boolean;
  onSave: (patch: JobPatchRequest) => Promise<void>;
}

interface FormState {
  source_url: string;
  company_name: string;
  job_title: string;
  contact_info: string;
  posting_date: string;
  deadline_date: string;
  applied_date: string;
  submission_link: string;
}

function toFormState(job: JobDetail): FormState {
  return {
    source_url: job.source_url ?? "",
    company_name: job.company_name ?? "",
    job_title: job.job_title ?? "",
    contact_info: job.contact_info ?? "",
    posting_date: job.posting_date ? job.posting_date.slice(0, 10) : "",
    deadline_date: job.deadline_date ? job.deadline_date.slice(0, 10) : "",
    applied_date: job.applied_date ? job.applied_date.slice(0, 10) : "",
    submission_link: job.submission_link ?? "",
  };
}

function toPatch(form: FormState): JobPatchRequest {
  return {
    source_url: form.source_url,
    company_name: form.company_name || null,
    job_title: form.job_title || null,
    contact_info: form.contact_info,
    posting_date: form.posting_date || null,
    deadline_date: form.deadline_date || null,
    applied_date: form.applied_date || null,
    submission_link: form.submission_link || null,
  };
}

const LABEL = "block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1";
const INPUT =
  "w-full px-3 py-2 text-sm rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-150";

interface DuplicateWarning {
  open: boolean;
  existingJob: DuplicateCheckResult["job"] | null;
  pendingPatch: JobPatchRequest | null;
}

const CLOSED_WARNING: DuplicateWarning = { open: false, existingJob: null, pendingPatch: null };

function buildDuplicateMessage(job: DuplicateCheckResult["job"] | null): string {
  if (!job?.title) return "A job with this URL already exists in your list.";
  if (job.company) return `You already have "${job.title}" at ${job.company} in your list.`;
  return `You already have "${job.title}" in your list.`;
}

export default function JobTab({ job, onDirtyChange, saving, onSave }: Props) {
  const [form, setForm] = useState<FormState>(() => toFormState(job));
  const [initial, setInitial] = useState<FormState>(() => toFormState(job));
  const [checking, setChecking] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState<DuplicateWarning>(CLOSED_WARNING);
  const contactRef = useRef<HTMLTextAreaElement>(null);

  function autoResizeContact() {
    const el = contactRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = el.scrollHeight + "px";
  }

  useEffect(() => {
    autoResizeContact();
  }, [form.contact_info]);

  const dirty = JSON.stringify(form) !== JSON.stringify(initial);

  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  // Re-sync when job prop changes (e.g. after successful save)
  useEffect(() => {
    const next = toFormState(job);
    setForm(next);
    setInitial(next);
  }, [job]);

  function set(field: keyof FormState, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const patch = toPatch(form);
    const newUrl = form.source_url.trim();
    const urlChanged = newUrl && newUrl !== (job.source_url ?? "");
    if (urlChanged) {
      setChecking(true);
      const result = await jobsService.checkDuplicate(newUrl);
      setChecking(false);
      if (result?.exists) {
        setDuplicateWarning({ open: true, existingJob: result.job ?? null, pendingPatch: patch });
        return;
      }
    }
    await onSave(patch);
  }

  function handleDuplicateConfirm() {
    const patch = duplicateWarning.pendingPatch;
    setDuplicateWarning(CLOSED_WARNING);
    if (patch) onSave(patch);
  }

  function handleDuplicateCancel() {
    setDuplicateWarning(CLOSED_WARNING);
  }

  return (
    <form
      id="job-tab-form"
      onSubmit={handleSubmit}
      className="py-6 px-2 space-y-5"
    >
      {/* Source URL */}
      <div>
        <label htmlFor="jt-source-url" className={LABEL}>
          Source URL
        </label>
        <input
          id="jt-source-url"
          type="url"
          aria-label="Source URL"
          value={form.source_url}
          onChange={(e) => set("source_url", e.target.value)}
          disabled={saving || checking}
          className={INPUT}
        />
      </div>

      {/* Company Name */}
      <div>
        <label htmlFor="jt-company" className={LABEL}>
          Company Name
        </label>
        <input
          id="jt-company"
          type="text"
          aria-label="Company Name"
          value={form.company_name}
          onChange={(e) => set("company_name", e.target.value)}
          disabled={saving}
          className={INPUT}
        />
      </div>

      {/* Job Title */}
      <div>
        <label htmlFor="jt-title" className={LABEL}>
          Job Title
        </label>
        <input
          id="jt-title"
          type="text"
          aria-label="Job Title"
          value={form.job_title}
          onChange={(e) => set("job_title", e.target.value)}
          disabled={saving}
          className={INPUT}
        />
      </div>

      {/* Contact Info */}
      <div>
        <label htmlFor="jt-contact" className={LABEL}>
          Contact Info
        </label>
        <textarea
          ref={contactRef}
          id="jt-contact"
          rows={1}
          aria-label="Contact Info"
          value={form.contact_info}
          onChange={(e) => set("contact_info", e.target.value)}
          disabled={saving}
          className={`${INPUT} resize-none overflow-hidden font-mono text-xs`}
        />
      </div>

      {/* Dates row */}
      <div className="flex flex-wrap gap-4">
        <div className="flex-1 min-w-0">
          <label htmlFor="jt-posting-date" className={LABEL}>
            Posting Date
          </label>
          <input
            id="jt-posting-date"
            type="date"
            aria-label="Posting Date"
            value={form.posting_date}
            onChange={(e) => set("posting_date", e.target.value)}
            disabled={saving}
            className={INPUT}
          />
        </div>
        <div className="flex-1 min-w-0">
          <label htmlFor="jt-deadline-date" className={LABEL}>
            Deadline
          </label>
          <input
            id="jt-deadline-date"
            type="date"
            aria-label="Deadline"
            value={form.deadline_date}
            onChange={(e) => set("deadline_date", e.target.value)}
            disabled={saving}
            className={INPUT}
          />
        </div>
        <div className="flex-1 min-w-0">
          <label htmlFor="jt-applied-date" className={LABEL}>
            Applied Date
          </label>
          <input
            id="jt-applied-date"
            type="date"
            aria-label="Applied Date"
            value={form.applied_date}
            onChange={(e) => set("applied_date", e.target.value)}
            disabled={saving}
            className={INPUT}
          />
        </div>
      </div>

      {/* Submission Link */}
      <div>
        <label htmlFor="jt-submission-link" className={LABEL}>
          Submission Link
        </label>
        <input
          id="jt-submission-link"
          type="url"
          aria-label="Submission Link"
          value={form.submission_link}
          onChange={(e) => set("submission_link", e.target.value)}
          disabled={saving || checking}
          className={INPUT}
        />
      </div>

      <ConfirmModal
        open={duplicateWarning.open}
        title="Job Already in Your List"
        message={buildDuplicateMessage(duplicateWarning.existingJob)}
        confirmLabel="Save anyway"
        cancelLabel="Cancel"
        onConfirm={handleDuplicateConfirm}
        onCancel={handleDuplicateCancel}
      />
    </form>
  );
}
